'use client';
import type { DiscountBanner, StockInfo } from '../../../product/index';
import { captureCause } from '../../../shared/utils/sentry';
import { Array, Cause, Exit, Option } from 'effect';
import { useState, useTransition } from 'react';
import type { MouseEvent } from 'react';
import { useIntl } from 'react-intl';

import { showErrorToast } from '@vs-shop/ui/toast';
import type { AnchoredAlertVariant } from '@vs-shop/ui/anchoredAlert';
import ProductAddToCartErrorPopupBottom from '../../server/product/card/ProductAddToCartErrorPopupBottom';
import ProductCouponClickable from '../../server/product/ProductCouponClickable';

import { addCouponWithProductVariantsToOrderAtomFn } from '../../../effect/atom/order/orderMutations';
import { openCartDrawerAtom } from '@vs-shop/engine/atom/ui/cartDrawer';
import { CartError } from '@vs-shop/engine/capability/cart/errors';
import { useAtomSet } from '@effect/atom-react';

type ErrorPopupState = {
  message: string;
  variant: AnchoredAlertVariant;
};

type Props = {
  productCode: string;
  productName: string;
  discountBanner: DiscountBanner | null;
  price: number;
  priceWithoutTax: number;
  stock: StockInfo;
};

export default function ProductCardCouponClickableWrapper({
  productCode,
  productName,
  discountBanner,
  price,
  priceWithoutTax,
  stock,
}: Props) {
  const [isPending, startTransition] = useTransition();
  const [errorPopup, setErrorPopup] = useState<Option.Option<ErrorPopupState>>(Option.none());

  const maxOrderable = stock.eshop.maxOrderable ?? 0;

  const intl = useIntl();

  const setIsCartDrawerOpen = useAtomSet(openCartDrawerAtom);
  const addProductVariantAndCouponToOrder = useAtomSet(addCouponWithProductVariantsToOrderAtomFn, {
    mode: 'promiseExit',
  });

  const onUseCoupon = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    startTransition(async () => {
      const couponCode = Option.flatMap(Option.fromNullishOr(discountBanner), (banner) =>
        Option.fromNullishOr(banner.promo)
      );
      const result = await addProductVariantAndCouponToOrder({
        couponCode,
        quantity: 1,
        product: { code: productCode, name: productName, price, priceWithoutTax },
      });

      const reportFailure = (
        cause: Cause.Cause<unknown>,
        action: string,
        productAdded: boolean
      ) => {
        captureCause(cause, {
          component: 'ProductCardCouponClickableWrapper',
          action,
          additionalData: {
            productCode,
            quantity: 1,
            couponCode: Option.getOrElse(couponCode, () => 'missing'),
          },
        });
        const error = Option.getOrUndefined(Cause.findErrorOption(cause));
        if (error instanceof CartError.Rejected) {
          Option.match(Array.head(error.messages), {
            onNone: () => showErrorToast(error.message),
            onSome: (message) => {
              if (productAdded) {
                showErrorToast(message);
                return;
              }
              setErrorPopup(Option.some({ message, variant: 'destructive' }));
            },
          });
          return;
        }
        showErrorToast(
          intl.formatMessage({
            id: 'error.orderMutation.generic',
            defaultMessage: 'Došlo k chybě při zpracování objednávky.',
          })
        );
      };

      Exit.match(result, {
        onFailure: (cause) => reportFailure(cause, 'Add product to cart failed', false),
        onSuccess: (outcome) => {
          setIsCartDrawerOpen(true);
          Exit.match(outcome.coupon, {
            onFailure: (cause) =>
              reportFailure(cause, 'Apply coupon after product add failed', true),
            onSuccess: () => {},
          });
        },
      });
    });
  };

  if (discountBanner === null || maxOrderable === 0) return null;

  return (
    <div className="relative w-full">
      <ProductCouponClickable
        showCodeName={false}
        price={price}
        priceWithCode={discountBanner.money}
        codeName={discountBanner.promo}
        onUse={onUseCoupon}
        isLoading={isPending}
      />

      {Option.isSome(errorPopup) ? (
        <ProductAddToCartErrorPopupBottom
          message={errorPopup.value.message}
          onClose={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setErrorPopup(Option.none());
          }}
          variant={errorPopup.value.variant}
        />
      ) : null}
    </div>
  );
}
